// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import clsx from 'clsx'
import HelpfulButton from 'components/community/HelpfulButton'
import { replyLinkHref } from 'lib/community/model'
import type { CommunityHelpfulAnswer } from 'lib/community/types'
import { ReplyLink } from './ReviewReplyRow'
import styles from './ReviewEngagementRow.module.css'

interface ReviewEngagementRowProps {
  noteId: string
  /** The review's author handle (no '@') — Helpful's own-review check and the link's name. */
  handle: string
  /** DealHunt's public `helpful_count`. Absent or null (a card payload, an aggregate stored
   *  before the phase-2 push): no Helpful control — never a made-up 0. */
  helpfulCount?: number | null
  /** The list's batched read of the reader's votes (`useHelpfulMine`), for this note. */
  helpfulKnown?: CommunityHelpfulAnswer
  /** The list's memory of DealHunt's answers, so a re-rendered card keeps the vote. */
  helpfulMemory?: Map<string, CommunityHelpfulAnswer>
  /** The review's thread (`communityThreadHref`); null → no "Reply →". */
  thread: string | null
  /** The host's analytics for the Reply link (its page's existing event). */
  onReply?: () => void
  className?: string
}

/**
 * "Helpful · Reply →" under an owner review on the PDP wall (owner funnel C11, 2026-10-09; the
 * card surfaces draw the reply-only `ReviewReplyRow`): the feed's Helpful control (`HelpfulButton`, same props as `FeedRow` — the public
 * count in the server HTML, the reader's own vote and own-review state after hydration) and the
 * way into the review's thread at its reply box (same tab on the server pass and phones, a new
 * tab on a desktop after mount — the accept result's convention).
 *
 * Public content: the same HTML for every visitor. No points, no reply count (no surface outside
 * the community carries `reply_count`). Renders nothing when it has neither half.
 */
const ReviewEngagementRow: React.VFC<ReviewEngagementRowProps> = ({
  noteId,
  handle,
  helpfulCount = null,
  helpfulKnown,
  helpfulMemory,
  thread,
  onReply,
  className,
}) => {
  const replyHref = replyLinkHref(thread)
  const helpful = typeof helpfulCount === 'number' && Number.isFinite(helpfulCount)
  if (!helpful && !replyHref) {
    return null
  }
  return (
    <div className={clsx(styles.row, styles.full, className)}>
      {helpful && (
        <HelpfulButton
          noteId={noteId}
          handle={handle}
          count={helpfulCount}
          memory={helpfulMemory}
          known={helpfulKnown}
          layout="text"
          className={styles.helpful}
        />
      )}
      {replyHref && <ReplyLink handle={handle} href={replyHref} onClick={onReply} />}
    </div>
  )
}

export default ReviewEngagementRow
